// Printable-ASCII editing independent of Pixi, the browser and the game.
export class EditModel {
  text = '';
  anchor = 0;
  caret = 0;
  maxLength = 64;
  filter: (c: string) => boolean = c => c >= ' ' && c <= '~';
  private undoStack: { text: string; anchor: number; caret: number }[] = [];
  private redoStack: { text: string; anchor: number; caret: number }[] = [];
  get start(): number { return Math.min(this.anchor, this.caret); }
  get end(): number { return Math.max(this.anchor, this.caret); }
  set(text: string): void {
    if (text === this.text) return;
    this.text = [...text].filter(this.filter).join('').slice(0, this.maxLength);
    this.anchor = this.caret = this.text.length;
  }
  private snapshot() { return { text: this.text, anchor: this.anchor, caret: this.caret }; }
  private remember(): void { this.undoStack.push(this.snapshot()); if (this.undoStack.length > 100) this.undoStack.shift(); this.redoStack = []; }
  insert(text: string): void {
    const accepted = [...text].filter(this.filter).join('').slice(0, Math.max(0, this.maxLength - this.text.length + this.end - this.start));
    if (!accepted) return;
    this.remember();
    const start = this.start;
    this.text = this.text.slice(0, start) + accepted + this.text.slice(this.end);
    this.anchor = this.caret = start + accepted.length;
  }
  word(pos: number, direction: number): number {
    if (direction < 0) { while (pos > 0 && this.text[pos - 1] === ' ') pos--; while (pos > 0 && this.text[pos - 1] !== ' ') pos--; }
    else { while (pos < this.text.length && this.text[pos] !== ' ') pos++; while (pos < this.text.length && this.text[pos] === ' ') pos++; }
    return pos;
  }
  move(direction: number, shift = false, word = false): void {
    const to = !shift && this.start !== this.end ? (direction < 0 ? this.start : this.end)
      : word ? this.word(this.caret, direction) : this.caret + direction;
    this.select(to, shift);
  }
  select(pos: number, shift = false): void { this.caret = Math.max(0, Math.min(this.text.length, pos)); if (!shift) this.anchor = this.caret; }
  selectAll(): void { this.anchor = 0; this.caret = this.text.length; }
  selectWord(pos: number): void {
    let a = Math.min(pos, this.text.length), b = a;
    while (a > 0 && /[A-Za-z0-9_]/.test(this.text[a - 1])) a--;
    while (b < this.text.length && /[A-Za-z0-9_]/.test(this.text[b])) b++;
    this.anchor = a; this.caret = b;
  }
  delete(direction: number, word = false): void {
    if (this.start === this.end) {
      const next = word ? this.word(this.caret, direction) : this.caret + direction;
      this.anchor = Math.max(0, Math.min(this.text.length, next));
    }
    if (this.start === this.end) return;
    this.remember();
    const start = this.start;
    this.text = this.text.slice(0, start) + this.text.slice(this.end);
    this.anchor = this.caret = start;
  }
  undo(redo = false): void {
    const from = redo ? this.redoStack : this.undoStack, to = redo ? this.undoStack : this.redoStack;
    const state = from.pop();
    if (state) { to.push(this.snapshot()); Object.assign(this, state); }
  }
  key(e: { key: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }): boolean {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 'a') this.selectAll();
    else if (mod && e.key.toLowerCase() === 'z') this.undo(e.shiftKey);
    else if (mod && e.key.toLowerCase() === 'y') this.undo(true);
    else if (e.key === 'ArrowLeft') this.move(-1, e.shiftKey, mod);
    else if (e.key === 'ArrowRight') this.move(1, e.shiftKey, mod);
    else if (e.key === 'Home') this.select(0, e.shiftKey);
    else if (e.key === 'End') this.select(this.text.length, e.shiftKey);
    else if (e.key === 'Backspace') this.delete(-1, mod);
    else if (e.key === 'Delete') this.delete(1, mod);
    else if (!mod && e.key.length === 1) this.insert(e.key);
    else return false;
    return true;
  }
}
